import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CellContext, createColumnHelper } from "@tanstack/react-table";
import _ from "lodash";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { ButtonToolbar, Col, Form, Row } from "react-bootstrap";
import { Link, useNavigate, useParams } from "react-router-dom";
import type { BillingController } from "../../../api/src/billing/billing.controller";
import type {
  BillingReport,
  BillingReportHousehold,
} from "../../../api/src/billing/lib";
import type { JobStatus } from "../../../api/src/jobs/lib";
import type { SerializedObject, UnpackResponse } from "../../../api/src/lib";
import Loading from "../Loading";
import { NotificationContext } from "../Notifications";
import { useQueryAllAccounts } from "../clients/account/lib";
import { useQueryHouseholds } from "../clients/household/lib";
import ActionButton from "../components/ActionButton";
import MobileActionsMenu from "../components/MobileActionsMenu";
import Content from "../components/Content";
import FormError from "../components/FormError";
import { RowExpander } from "../components/Table/RowExpander";
import { Table, metaDefault, useTable } from "../components/Table/Table";
import {
  deserializeDate,
  useAuthenticatedFetch,
  useAuthenticatedFileFetch,
  useAuthenticatedMutation,
} from "../lib/api";
import { DateWithoutTime } from "../lib/date";
import { boolToYesNo, capitalize, displayAccountName } from "../lib/display";
import { formatCurrency, formatPercent } from "../lib/numbers";
import BillingFinalizeWorkflow from "./BillingFinalizeWorkflow";
import FinalizeConfirmation from "./FinalizeConfirmation";
import { useQueryExportBillingReport } from "./lib";

/**
 * Whether the report carries the figures this page shows — the billable AUM,
 * fee, exclusion and split fields, the download times, and the households and
 * splits tables.
 *
 * `complete` and `finalized` are the two statuses that have them, and they
 * carry the same figures: finalizing freezes what the report already computed,
 * it does not recompute anything
 * (s-billing-report-finalization-capability-019fba175c3d304d, §The finalized
 * state). Every other status means the job that produces those figures has not
 * finished, so the page shows the raw status instead.
 *
 * `BillingReportsTable` imports this and `reportStateLabel` from here, so a
 * report's row and its page answer both questions the same way.
 */
export function hasFigures(status: JobStatus) {
  return status === "complete" || status === "finalized";
}

/**
 * The words the advisor reads for the report's state (R27): a `complete`
 * report is a draft, a `finalized` one is finalized. They must read the same
 * here as on the reports list row (AC24). The terms are the dq / Jon
 * conversation of 2026-07-31, quoted in
 * `doc-finalizing-a-billing-report-019fb971b779605f`.
 *
 * A report that is still running, or that failed or was canceled, is neither —
 * it has no state to show yet, and none of the figures either.
 */
export function reportStateLabel(status: JobStatus) {
  if (status === "finalized") return "Finalized";
  if (status === "complete") return "Draft";
  return null;
}

type BillingHouseholdRow = {
  type: "household" | "account";
  id?: number | null;
  name?: string;
  accounts?: BillingHouseholdRow[];
  totalAUM: number;
  totalFee: number;
  totalDebited?: number;
  effectiveFeeRate: number;
  totalAssetAdjustmentDollarAmount: number;
  totalFeeSplit: number;
  startDate?: Date;
  endDate?: Date;
  relocatedAccountId?: number | null;
  relocatedAccountName?: string;
  // One row type serves both households and accounts. A household's `totalCash`
  // is a number or null — null on the group standing for the accounts with no
  // household (`api/src/billing/lib.ts:67`) — and an account's is a number.
  // CashMinusDebited coalesces either to 0 (`:229`), so the rendered figure is
  // unchanged by the null.
  totalCash?: number | null;
  billableBalance?: number;
  effectiveFeeBalance?: number;
};

const householdColumnHelper = createColumnHelper<BillingHouseholdRow>();
const cashMinusDebitedCellRender = (
  info: CellContext<BillingHouseholdRow, unknown>,
) => {
  const value = info.getValue() as number;
  const className = value < 0 ? "text-end text-danger" : "text-end";
  return <span className={className}>{formatCurrency(value, 2)}</span>;
};

const BillingHouseholdsTable = ({
  households,
  viewMode,
}: {
  households: BillingReportHousehold[];
  viewMode: string;
}) => {
  const viewModeRef = useRef(viewMode);

  useEffect(() => {
    viewModeRef.current = viewMode;
  }, [viewMode]);

  const columns = useMemo(
    () => [
      householdColumnHelper.accessor("name", {
        cell: (info) => (
          <div
            style={{
              // Since rows are flattened by default,
              // we can use the row.depth property
              // and paddingLeft to visually indicate the depth
              // of the row
              paddingLeft: `${info.row.depth * 2}rem`,
            }}
          >
            {info.row.original.type === "household" &&
            typeof info.row.original.id === "undefined" ? (
              <em>Unassigned</em>
            ) : (
              <Link
                to={`/clients/${info.row.original.type}s/${info.row.original.id}`}
              >
                {info.getValue() ?? ""}
              </Link>
            )}
          </div>
        ),
        aggregatedCell: (info) => (
          <>
            {info.row.getCanExpand() ? <RowExpander row={info.row} /> : ""}{" "}
            <Link
              to={`/clients/${info.row.original.type}s/${info.row.original.id}`}
            >
              {info.getValue()} {viewModeRef.current}
            </Link>
          </>
        ),
        header: () => "Name",
      }),
      householdColumnHelper.accessor((row) => row.totalAUM ?? 0, {
        id: "totalAUM",
        cell: (info) => formatCurrency(info.getValue(), 2),
        aggregatedCell: (info) => formatCurrency(info.getValue(), 2),
        header: () => "AUM",
        meta: {
          className: "text-end",
          headerClassName: "text-end",
        },
        enableColumnFilter: false,
      }),
      householdColumnHelper.accessor(
        (row) =>
          row.billableBalance ??
          row.totalAUM - row.totalAssetAdjustmentDollarAmount,
        {
          id: "billableBalance",
          cell: (info) => formatCurrency(info.getValue(), 2),
          aggregatedCell: (info) => formatCurrency(info.getValue(), 2),
          header: () => "Billable Balance",
          meta: {
            className: "text-end",
            headerClassName: "text-end",
          },
          enableColumnFilter: false,
        },
      ),
      householdColumnHelper.accessor(
        (row) => row.effectiveFeeBalance ?? row.totalAUM,
        {
          id: "effectiveFeeBalance",
          cell: (info) => formatCurrency(info.getValue(), 2),
          aggregatedCell: (info) => formatCurrency(info.getValue(), 2),
          header: () => "Effective Fee Balance",
          meta: {
            className: "text-end",
            headerClassName: "text-end",
          },
          enableColumnFilter: false,
        },
      ),
      householdColumnHelper.accessor("totalFee", {
        cell: (info) => formatCurrency(info.getValue(), 2),
        aggregatedCell: (info) => formatCurrency(info.getValue(), 2),
        header: () => "Fee",
        meta: {
          className: "text-end",
          headerClassName: "text-end",
        },
        enableColumnFilter: false,
      }),
      householdColumnHelper.accessor(
        (row) => row.totalDebited ?? row.totalFee,
        {
          id: "totalDebited",
          cell: (info) => formatCurrency(info.getValue(), 2),
          aggregatedCell: (info) => formatCurrency(info.getValue(), 2),
          header: () => "Debited",
          meta: {
            className: "text-end",
            headerClassName: "text-end",
          },
          enableColumnFilter: false,
        },
      ),
      householdColumnHelper.accessor(
        (row) => (row.totalCash ?? 0) - (row.totalDebited ?? row.totalFee),
        {
          id: "cashMinusDebited",
          cell: cashMinusDebitedCellRender,
          aggregatedCell: cashMinusDebitedCellRender,
          header: () => "CashMinusDebited",
          meta: {
            className: "text-end",
            headerClassName: "text-end",
          },
          enableColumnFilter: false,
        },
      ),
      // On 0 to 100 on the wire (`billingReportPercents`,
      // `api/src/billing/billing.controller.ts`); `formatPercent` takes 0 to 1.
      householdColumnHelper.accessor("effectiveFeeRate", {
        cell: (info) => formatPercent(info.getValue() / 100, 2),
        aggregatedCell: (info) => formatPercent(info.getValue() / 100, 3),
        header: () => "Effective Fee Rate",
        meta: {
          className: "text-end",
          headerClassName: "text-end",
        },
        enableColumnFilter: false,
      }),
      householdColumnHelper.accessor("totalAssetAdjustmentDollarAmount", {
        cell: (info) => formatCurrency(info.getValue(), 2),
        aggregatedCell: (info) => formatCurrency(info.getValue(), 2),
        header: () => "Asset Exclusions",
        meta: {
          className: "text-end",
          headerClassName: "text-end",
        },
        enableColumnFilter: false,
      }),
      householdColumnHelper.accessor("totalFeeSplit", {
        cell: (info) => formatCurrency(info.getValue(), 2),
        aggregatedCell: (info) => formatCurrency(info.getValue(), 2),
        header: () => "Fee Splits",
        meta: {
          className: "text-end",
          headerClassName: "text-end",
        },
        enableColumnFilter: false,
      }),
      householdColumnHelper.accessor("startDate", {
        cell: (info) => info.getValue()?.toLocaleDateString(),
        header: () => "Start Date",
      }),
      householdColumnHelper.accessor("endDate", {
        cell: (info) => info.getValue()?.toLocaleDateString(),
        header: () => "End Date",
      }),
      householdColumnHelper.accessor((row) => row.relocatedAccountName ?? "", {
        id: "relocatedAccountName",
        cell: (info) =>
          info.row.original.relocatedAccountId == null ? null : (
            <Link
              to={`/clients/accounts/${info.row.original.relocatedAccountId}`}
            >
              {info.getValue()}
            </Link>
          ),
        header: () => "Relocated To",
      }),
    ],
    [],
  );

  const {
    isPending: isPendingAccounts,
    isError: isErrorAccounts,
    data: dataAccounts,
  } = useQueryAllAccounts({ includeInactive: true });

  const {
    isPending: isPendingHouseholds,
    isError: isErrorHouseholds,
    data: dataHouseholds,
  } = useQueryHouseholds();

  const accountsMap = useMemo(
    () => _.keyBy(dataAccounts?.data ?? [], "id"),
    [dataAccounts?.data],
  );

  const householdsMap = useMemo(
    () => _.keyBy(dataHouseholds?.data ?? [], "id"),
    [dataHouseholds?.data],
  );

  const householdRows: BillingHouseholdRow[] = useMemo(
    () =>
      households.map((household) => ({
        ...household,
        type: "household",
        id: household.householdId,
        name:
          household.householdId == null
            ? undefined
            : householdsMap[household.householdId]?.name,
        accounts: household.accounts.map((account): BillingHouseholdRow => ({
          ...account,
          type: "account",
          id: account.accountId,
          name: displayAccountName(
            accountsMap[account.accountId]?.displayName,
            accountsMap[account.accountId]?.displayNumber,
          ),
          relocatedAccountName:
            account.relocatedAccountId == null
              ? undefined
              : displayAccountName(
                  accountsMap[account.relocatedAccountId]?.displayName,
                  accountsMap[account.relocatedAccountId]?.displayNumber,
                ),
          startDate: account.startDate,
          endDate: account.endDate,
        })),
      })),
    [accountsMap, households, householdsMap],
  );

  const accountRows: BillingHouseholdRow[] = useMemo(
    () =>
      householdRows.flatMap(
        (household) =>
          household?.accounts?.map((account): BillingHouseholdRow => ({
            ...account,
            type: "account",
            id: account.id,
            name: account.name,
            relocatedAccountName: account.relocatedAccountName,
            startDate: account.startDate,
            endDate: account.endDate,
          })) || [],
      ),
    [householdRows],
  );

  const { table } = useTable({
    columns,
    data: viewMode === "Household" ? householdRows : accountRows,
    getRowId: (row) => `${row.type}-${row.id ?? ""}`,
    getSubRows: (row) => (row.accounts ?? []) as BillingHouseholdRow[],
    autoResetPageIndex: true,
    meta: metaDefault,
    initialState: {
      sorting: [{ id: "name", desc: false }],
    },
  });

  return isPendingHouseholds || isPendingAccounts ? (
    <Loading />
  ) : isErrorHouseholds || isErrorAccounts ? (
    <FormError message="Failed to load households and accounts" />
  ) : (
    <Table table={table} />
  );
};

type BillingSplitRow = {
  amount: number;
  name: string;
  splitterName: string;
};

const splitColumnHelper = createColumnHelper<BillingSplitRow>();

const BillingSplitsTable = ({
  households,
}: {
  households: BillingReportHousehold[];
}) => {
  const columns = useMemo(
    () => [
      splitColumnHelper.accessor("name", {
        header: () => "Name",
      }),
      splitColumnHelper.accessor("splitterName", {
        header: () => "Splitter",
        enableColumnFilter: false,
      }),
      splitColumnHelper.accessor("amount", {
        cell: (info) => formatCurrency(info.getValue(), 2),
        header: () => "Total",
        enableColumnFilter: false,
      }),
    ],
    [],
  );

  const rows: BillingSplitRow[] = useMemo(() => {
    const billingSplits = households
      .flatMap((household) => household.accounts)
      .flatMap((account) => account.billingSplits);

    const combinedBillingSplits = _.groupBy(billingSplits, "name");

    return Object.entries(combinedBillingSplits).map(([name, splits]) => ({
      name,
      splitterName: splits[0].splitterName,
      amount: splits.reduce((sum, split) => sum + split.amount, 0),
    }));
  }, [households]);

  const { table } = useTable({
    columns,
    data: rows,
    autoResetPageIndex: true,
    meta: metaDefault,
    initialState: {
      sorting: [{ id: "name", desc: false }],
    },
  });

  return <Table table={table} />;
};

const BillingReportInfo = () => {
  const { reportId } = useParams();

  const {
    isPending: isPendingBillingReports,
    isError: isErrorBillingReports,
    data: billingReportBody,
  } = useAuthenticatedFetch<
    SerializedObject<UnpackResponse<BillingController["getBillingReport"]>>
  >(`/v1/billing/reports/${reportId}`);

  const billingReport: BillingReport | undefined = useMemo(() => {
    if (typeof billingReportBody?.data !== "undefined") {
      return deserializeBillingReport(billingReportBody.data);
    }
  }, [billingReportBody?.data]);

  const { isLoading: isLoadingInvoices, refetch: downloadInvoices } =
    useAuthenticatedFileFetch(
      `/billing/reports/${reportId}/invoices`,
      { method: "POST" },
      {
        enabled: false,
      },
    );

  const { isLoading: isLoadingFeeUploadFile, refetch: downloadFeeUploadFile } =
    useAuthenticatedFileFetch(
      `/billing/reports/${reportId}/fee-upload`,
      { method: "POST" },
      {
        enabled: false,
      },
    );

  const deleteReport = useAuthenticatedMutation(
    `/v1/billing/reports/${reportId}`,
    { method: "DELETE" },
  );

  const navigate = useNavigate();
  const notificationContext = useContext(NotificationContext);
  const queryClient = useQueryClient();

  const [showFinalizeConfirmation, setShowFinalizeConfirmation] =
    useState(false);

  const hideFinalizeConfirmation = useCallback(
    () => setShowFinalizeConfirmation(false),
    [],
  );

  /**
   * Re-read the report once it has been finalized, so the State field and the
   * Finalize and Delete controls all reflect the new status. The workflow's
   * steps run on after the request returns (R30); nothing here waits for them.
   */
  const onFinalized = useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: [`/v1/billing/reports/${reportId}`],
    });
  }, [queryClient, reportId]);

  const deleteReportMutation = useMutation({
    mutationFn: async () => {
      await deleteReport();
      notificationContext.pushNotification({
        id: `billing-report-${reportId}`,
        header: "Billing Report Deleted",
        body: `Billing report for ${billingReport?.endDate.toLocaleDateString()} deleted`,
        variant: "warning",
      });
      navigate("..");
    },
  });

  const ViewModeSelector = ({
    viewMode,
    setViewMode,
  }: {
    viewMode: string;
    setViewMode: (val: string) => void;
  }) => {
    const handleChangeViewMode = useCallback(
      (e: React.ChangeEvent<HTMLSelectElement>) => {
        setViewMode(e.target.value);
      },
      [setViewMode],
    );

    return (
      <Col sm={8} md={6} xl={4} xxl={3} className="mb-3">
        <Form.Group controlId="form-calculation-type">
          <Form.Label>View by</Form.Label>
          <Form.Select value={viewMode} onChange={handleChangeViewMode}>
            {["Household", "Account"].map((mode) => (
              <option key={mode} value={mode}>
                {mode}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
      </Col>
    );
  };
  const [viewMode, setViewMode] = useState("Household");
  const viewModeSelector = (
    <ViewModeSelector viewMode={viewMode} setViewMode={setViewMode} />
  );

  const { isLoading: isLoadingExport, refetch: refetchExport } =
    useQueryExportBillingReport(parseInt(reportId ?? ""));

  const onExport = useCallback(async () => {
    await refetchExport();
  }, [refetchExport]);

  return isPendingBillingReports ? (
    <Loading />
  ) : isErrorBillingReports ? (
    <FormError message="Failed to load billing report" />
  ) : typeof billingReport === "undefined" ? (
    <FormError message="Billing Report not found" />
  ) : (
    <>
      <Row>
        <Col>
          <h1>Billing Report Details</h1>
        </Col>
        <Col md="auto">
          <div className="d-flex justify-content-end mb-3 d-lg-none">
            <MobileActionsMenu
              actions={[
                {
                  label: "Download Invoices",
                  icon: "/icons/audit.svg",
                  onClick: () => downloadInvoices(),
                  disabled: isLoadingInvoices,
                },
                {
                  label: "Download Fee Upload",
                  icon: "/icons/buy.svg",
                  onClick: () => downloadFeeUploadFile(),
                  disabled: isLoadingFeeUploadFile,
                },
                {
                  label: "Export",
                  icon: "/icons/folded-list.svg",
                  onClick: onExport,
                  disabled: isLoadingExport,
                },
                ...(billingReport.status !== "complete"
                  ? []
                  : [
                      {
                        label: "Finalize",
                        icon: "/icons/approve.svg",
                        onClick: () => setShowFinalizeConfirmation(true),
                      },
                    ]),
                ...(billingReport.status === "finalized"
                  ? []
                  : [
                      {
                        label: "Delete",
                        icon: "/icons/trash.svg",
                        onClick: () => deleteReportMutation.mutate(),
                        disabled: deleteReportMutation.isPending,
                      },
                    ]),
              ]}
            />
          </div>
          <ButtonToolbar className="d-none d-lg-flex mb-3 justify-content-end">
            <ActionButton
              variant="secondary"
              label="Download Invoices"
              icon="/icons/audit.svg"
              onClick={() => downloadInvoices()}
              className="me-2"
              disabled={isLoadingInvoices}
            />
            <ActionButton
              variant="secondary"
              label="Download Fee Upload"
              icon="/icons/buy.svg"
              onClick={() => downloadFeeUploadFile()}
              className="me-2"
              disabled={isLoadingFeeUploadFile}
            />
            <ActionButton
              variant="secondary"
              label="Export"
              icon="/icons/folded-list.svg"
              onClick={onExport}
              className="me-2"
              disabled={isLoadingExport}
            />
            {/*
              Only a draft can be finalized: `complete` is the sole status the
              transition starts from (R1). A finalized report shows that in the
              State field instead — there is no way back out of `finalized` for
              it to offer (R7).
            */}
            {billingReport.status !== "complete" ? null : (
              <ActionButton
                variant="secondary"
                label="Finalize"
                icon="/icons/approve.svg"
                onClick={() => setShowFinalizeConfirmation(true)}
                className="me-2"
              />
            )}
            {/*
              R6: a finalized report offers no delete control. This is defence
              in depth only — the service refuses the delete (R5), and must keep
              refusing it however this renders.
            */}
            {billingReport.status === "finalized" ? null : (
              <ActionButton
                variant="secondary"
                label="Delete"
                icon="/icons/trash.svg"
                onClick={() => deleteReportMutation.mutate()}
                disabled={deleteReportMutation.isPending}
              />
            )}
          </ButtonToolbar>
        </Col>
      </Row>
      {/*
        The same confirmation the reports list renders, so the advisor reads
        the same words and the same finalize request is sent whichever surface
        they pressed Finalize on (R26).
      */}
      {billingReport.status !== "complete" ? null : (
        <FinalizeConfirmation
          show={showFinalizeConfirmation}
          reportId={billingReport.id}
          onHide={hideFinalizeConfirmation}
          onFinalized={onFinalized}
        />
      )}
      <Content>
        <Row>
          <Col xxl={2} md={4} sm={6} className="mb-2">
            <Form.Group controlId="form-period">
              <Form.Label>Billing Date</Form.Label>
              <Form.Control
                type="text"
                value={billingReport.endDate.toLocaleDateString()}
                plaintext
                readOnly
              />
            </Form.Group>
          </Col>
          <Col xl={2} md={4} sm={6} className="mb-2">
            <Form.Group controlId="form-isFirmWideExecution">
              <Form.Label>Is Firm-Wide Execution?</Form.Label>
              <Form.Control
                type="text"
                value={boolToYesNo(billingReport.isFirmWideExecution)}
                plaintext
                readOnly
              />
            </Form.Group>
          </Col>
          {!hasFigures(billingReport.status) ? (
            <Col xl={2} md={4} sm={6} className="mb-2">
              <Form.Group controlId="form-status">
                <Form.Label>Status</Form.Label>
                <Form.Control
                  type="text"
                  value={capitalize(billingReport.status)}
                  plaintext
                  readOnly
                />
              </Form.Group>
            </Col>
          ) : (
            <>
              {/*
                R27 / AC24: the page says whether the report is a draft or
                finalized without the advisor having to do anything, in the
                same words the reports list row uses.
              */}
              <Col xl={2} md={4} sm={6} className="mb-2">
                <Form.Group controlId="form-reportState">
                  <Form.Label>State</Form.Label>
                  <Form.Control
                    type="text"
                    value={reportStateLabel(billingReport.status) ?? ""}
                    plaintext
                    readOnly
                  />
                </Form.Group>
              </Col>
              <Col xl={2} md={4} sm={6} className="mb-2">
                <Form.Group controlId="form-aum">
                  <Form.Label>Billable AUM</Form.Label>
                  <Form.Control
                    type="text"
                    value={formatCurrency(billingReport.billableAUM, 2)}
                    plaintext
                    readOnly
                  />
                  <Form.Text>
                    Total AUM: {formatCurrency(billingReport.totalAUM, 2)}
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col xl={2} md={4} sm={6} className="mb-2">
                <Form.Group controlId="form-debited">
                  <Form.Label>Total Debited</Form.Label>
                  <Form.Control
                    type="text"
                    value={formatCurrency(billingReport.totalFee, 2)}
                    plaintext
                    readOnly
                  />
                </Form.Group>
              </Col>
              <Col xl={2} md={4} sm={6} className="mb-2">
                <Form.Group controlId="form-effectiveFeeRate">
                  <Form.Label>Effective Fee Rate</Form.Label>
                  <Form.Control
                    type="text"
                    value={formatPercent(
                      billingReport.effectiveFeeRate / 100,
                      2,
                    )}
                    plaintext
                    readOnly
                  />
                </Form.Group>
              </Col>
              <Col xl={2} md={4} sm={6} className="mb-2">
                <Form.Group controlId="form-totalAssetAdjustmentDollarAmount">
                  <Form.Label>Total Asset Exclusions</Form.Label>
                  <Form.Control
                    type="text"
                    value={formatCurrency(
                      billingReport.totalAssetAdjustmentDollarAmount,
                      2,
                    )}
                    plaintext
                    readOnly
                  />
                </Form.Group>
              </Col>
              <Col xl={2} md={4} sm={6} className="mb-2">
                <Form.Group controlId="form-totalRelocatedFees">
                  <Form.Label>Total Relocated Fees</Form.Label>
                  <Form.Control
                    type="text"
                    value={formatCurrency(billingReport.totalRelocatedFees, 2)}
                    plaintext
                    readOnly
                  />
                </Form.Group>
              </Col>
              <Col xl={2} md={4} sm={6} className="mb-2">
                <Form.Group controlId="form-totalFeeSplit">
                  <Form.Label>Total Fee Splits</Form.Label>
                  <Form.Control
                    type="text"
                    value={formatCurrency(billingReport.totalFeeSplit ?? 0, 2)}
                    plaintext
                    readOnly
                  />
                </Form.Group>
              </Col>
            </>
          )}
          <Col xxl={2} md={4} sm={6} className="mb-2">
            <Form.Group controlId="form-createdTime">
              <Form.Label>Created Date</Form.Label>
              <Form.Control
                type="text"
                value={billingReport.createdTime.toLocaleString()}
                plaintext
                readOnly
              />
            </Form.Group>
          </Col>
          {!hasFigures(billingReport.status) ? null : (
            <>
              <Col xxl={2} md={4} sm={6} className="mb-2">
                <Form.Group controlId="form-latestInvoiceDownloadTime">
                  <Form.Label>Invoices Download Time</Form.Label>
                  <Form.Control
                    type="text"
                    value={
                      billingReport.latestInvoiceDownloadTime?.toLocaleString() ??
                      "N/A"
                    }
                    plaintext
                    readOnly
                  />
                </Form.Group>
              </Col>
              <Col xxl={2} md={4} sm={6} className="mb-2">
                <Form.Group controlId="form-latestCustodianFeeFileDownloadTime">
                  <Form.Label>Fee File Download Time</Form.Label>
                  <Form.Control
                    type="text"
                    value={
                      billingReport.latestCustodianFeeFileDownloadTime?.toLocaleString() ??
                      "N/A"
                    }
                    plaintext
                    readOnly
                  />
                </Form.Group>
              </Col>
            </>
          )}
        </Row>
        {/*
          Only a finalized report has a finalize workflow: the workflow is what
          one press of Finalize starts, so a draft has no steps and no attempts
          to show. Above the households table because it is the recovery
          surface — a step that failed is what the advisor came here to act on.
        */}
        {billingReport.status !== "finalized" ? null : (
          <BillingFinalizeWorkflow reportId={billingReport.id} />
        )}
        {!hasFigures(billingReport.status) ? null : (
          <>
            <Row>
              {viewModeSelector}
              <>
                <h2>{viewMode === "Household" ? "Households" : "Accounts"}</h2>
                <BillingHouseholdsTable
                  households={billingReport.households}
                  viewMode={viewMode}
                />
              </>
            </Row>
            {billingReport.households
              .flatMap((household) => household.accounts)
              .flatMap((account) => account.billingSplits).length <=
            0 ? null : (
              <Row className="mt-3">
                <h2>Billing Splits</h2>
                <BillingSplitsTable households={billingReport.households} />
              </Row>
            )}
          </>
        )}
      </Content>
    </>
  );
};

export default BillingReportInfo;

export function deserializeBillingReport(
  billingReport: SerializedObject<BillingReport>,
) {
  return {
    ...billingReport,
    startDate: new DateWithoutTime(billingReport.startDate),
    endDate: new DateWithoutTime(billingReport.endDate),
    createdTime: deserializeDate(billingReport.createdTime),
    latestCustodianFeeFileDownloadTime: deserializeDate(
      billingReport.latestCustodianFeeFileDownloadTime,
    ),
    latestInvoiceDownloadTime: deserializeDate(
      billingReport.latestInvoiceDownloadTime,
    ),
    finalizedAt: deserializeDate(billingReport.finalizedAt),
    households: billingReport.households.map((household) => ({
      ...household,
      accounts: household.accounts.map((account) => ({
        ...account,
        startDate: new DateWithoutTime(account.startDate),
        endDate: new DateWithoutTime(account.endDate),
      })),
    })),
  };
}
